import { Button } from '@/components/ui/button';
import intersperseWith from '@/lib/intersperse';
import { Loader2Icon } from 'lucide-react';
import React from 'react';
import { FieldValues, FormProvider, UseFormReturn } from 'react-hook-form';
import { GroupedFields } from './fields/types';
import { FormField } from './FormField';

interface EntityFormProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  formMode: 'loading' | 'view' | 'edit';
  setFormMode: (mode: 'view' | 'edit') => void;
  groupedFields: GroupedFields;
  title?: string;
  description?: string;
  onSubmit?: React.FormEventHandler<HTMLFormElement>;
  onCancel?: () => boolean | void;
  hideActions?: boolean;
  id?: string;
}

export function EntityForm<T extends FieldValues>({
  form,
  formMode,
  setFormMode,
  groupedFields,
  onSubmit,
  onCancel,
  hideActions = false,
  id,
}: EntityFormProps<T>) {
  const handleCancel = () => {
    if (onCancel?.() ?? true) {
      form.reset();
      setFormMode('view');
    }
  };

  return (
    <FormProvider {...form}>
      <form id={id} onSubmit={onSubmit}>
        <div className="bg-form-surface -mx-2 -mt-2 flex flex-col gap-1 p-2 md:-mx-4 md:-mt-4 md:p-4">
          {intersperseWith(
            (i) => (
              <div key={i} className="bg-border -mx-2 h-px md:-mx-4" />
            ),
            Object.entries(groupedFields).map(([groupName, fields]) => (
              <fieldset key={groupName}>
                <div className="grid grid-cols-1 gap-x-4 gap-y-1 text-sm @md:grid-cols-2">
                  {fields.map((field) => (
                    <FormField
                      key={field.name}
                      field={field}
                      formMode={formMode}
                    />
                  ))}
                </div>
              </fieldset>
            )),
          )}
        </div>
        {!hideActions && (
          <div
            data-slot="alert-dialog-footer"
            className="rev mt-4 flex flex-col-reverse gap-2 @sm:flex-row-reverse"
          >
            {formMode === 'view' && (
              <Button onClick={() => setFormMode('edit')}>Edit</Button>
            )}
            {formMode === 'edit' && (
              <>
                <Button
                  type="submit"
                  disabled={
                    !form.formState.isDirty || form.formState.isSubmitting
                  }
                >
                  {form.formState.isSubmitting ? (
                    <>
                      <Loader2Icon className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
                <Button variant="ghost" type="button" onClick={handleCancel}>
                  Cancel
                </Button>
              </>
            )}
          </div>
        )}
      </form>
    </FormProvider>
  );
}
